import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { apiClient, upsertSavedAccount, cn, getSupabase } from '@dogdisk/shared'
import { MobileScreen, TopBar } from '../../components/mobile-screen'

const schema = z.object({
  method: z.enum(['totp', 'email', 'backup_code']),
  code: z.string().min(6),
})

type VerifyResp = {
  access_token: string
  refresh_token: string
  user_id: string
  username: string
  avatar_url: string | null
}

type Method = 'totp' | 'email'

export function TwoFactorPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const pid = params.get('pid') ?? ''
  const next = params.get('next') ?? '/'

  const [method, setMethod] = useState<Method>('totp')
  const [code, setCode] = useState<string[]>(['', '', '', '', '', ''])
  const [busy, setBusy] = useState(false)
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [resendIn, setResendIn] = useState(60)

  const inputs = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    if (resendIn <= 0) return
    const t = setTimeout(() => setResendIn((v) => v - 1), 1000)
    return () => clearTimeout(t)
  }, [resendIn])

  const setDigit = (i: number, v: string) => {
    const ch = v.replace(/\D/g, '').slice(-1)
    setCode((prev) => {
      const next = [...prev]
      next[i] = ch
      return next
    })
    if (ch && i < 5) inputs.current[i + 1]?.focus()
  }

  const sendEmailCode = async () => {
    setErrMsg(null)
    try {
      await apiClient.post('/gapi/public/auth/2fa/email/send', { mfa_pending_id: pid })
      setResendIn(60)
    } catch (e) {
      setErrMsg(e instanceof Error ? e.message : '发送失败')
    }
  }

  const verify = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErrMsg(null)
    try {
      const codeStr = code.join('')
      const parsed = schema.parse({ method, code: codeStr })
      const res = await apiClient.post<VerifyResp>('/gapi/public/auth/2fa/verify', {
        mfa_pending_id: pid,
        ...parsed,
      })
      await getSupabase().auth.setSession({
        access_token: res.access_token,
        refresh_token: res.refresh_token,
      })
      // 2FA verify 路径不知道用户原密码;saved-account password 留空,
      // 下次点该行 saved 只会自动填用户名,密码需用户手动输入
      upsertSavedAccount({
        user_id: res.user_id,
        username: res.username,
        avatar_url: res.avatar_url,
        password: '',
        last_login_at: new Date().toISOString(),
      })
      navigate(next)
    } catch (err) {
      setErrMsg(err instanceof Error ? err.message : '验证失败')
    } finally {
      setBusy(false)
    }
  }

  if (!pid) {
    return (
      <MobileScreen>
        <TopBar title="两步验证" fallback="/auth/login" />
        <main className="flex flex-col gap-4 px-6 pt-4">
          <p className="text-[13px] text-[#FF6B6B]">缺少 mfa_pending_id 参数</p>
          <button
            onClick={() => navigate('/auth/login')}
            className="h-12 rounded-lg bg-[#19caad] text-[15px] font-semibold text-white"
          >
            返回登录
          </button>
        </main>
      </MobileScreen>
    )
  }

  return (
    <MobileScreen bg="surface">
      <TopBar title="两步验证" />
      <main data-testid="tqcKM" className="flex flex-col gap-7 px-6 pt-4">
        <div className="flex flex-col items-center gap-3 pt-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#ebfaf6]">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#19caad" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
            </svg>
          </div>
          <h2 className="text-[20px] font-bold text-[#1A1A1A]">请输入验证码</h2>
          <p className="text-center text-[13px] text-[#9CA3AF]">
            我们已向您的{method === 'totp' ? '手机 Authenticator' : '邮箱'}发送 6 位验证码
          </p>
        </div>

        <form onSubmit={verify} className="flex flex-col gap-6">
          <div className="flex justify-center gap-2">
            {code.map((d, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputs.current[i] = el
                }}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && !d && i > 0) inputs.current[i - 1]?.focus()
                }}
                inputMode="numeric"
                maxLength={1}
                className="h-12 w-11 rounded-lg border-2 border-[#19caad]/40 bg-white text-center text-[18px] font-semibold text-[#1A1A1A] outline-none focus:border-[#19caad]"
              />
            ))}
          </div>

          <div className="flex justify-center gap-2">
            <button
              type="button"
              onClick={() => setMethod('totp')}
              className={cn(
                'rounded-lg px-4 py-2 text-[12px] font-medium',
                method === 'totp' ? 'bg-[#19caad] text-white' : 'bg-[#F1F2F4] text-[#9CA3AF]',
              )}
            >
              Authenticator (TOTP)
            </button>
            <button
              type="button"
              onClick={() => setMethod('email')}
              className={cn(
                'rounded-lg px-4 py-2 text-[12px] font-medium',
                method === 'email' ? 'bg-[#19caad] text-white' : 'bg-[#F1F2F4] text-[#9CA3AF]',
              )}
            >
              使用邮箱验证码
            </button>
          </div>

          {method === 'email' && (
            <button
              type="button"
              onClick={sendEmailCode}
              disabled={resendIn > 0}
              className="self-center text-[13px] text-[#9CA3AF] disabled:opacity-60"
            >
              {resendIn > 0 ? `重新发送 (${resendIn}s)` : '发送邮箱验证码'}
            </button>
          )}

          {errMsg && <p className="text-center text-[13px] text-[#FF6B6B]">{errMsg}</p>}

          <button
            type="submit"
            disabled={busy || code.some((c) => !c)}
            className="h-12 rounded-lg bg-[#19caad] text-[15px] font-semibold text-white active:bg-[#14a18b] disabled:opacity-50"
          >
            {busy ? '验证中…' : '验证'}
          </button>

          {/* Phase 4-D4 VwOOZ:"使用备用方式" alt 含 TOTP/email 切换 + backup code 入口
              · 短按:totp↔email 切换(保留原行为)
              · 备用恢复码:跳 /profile/mfa/backup-codes(VwOOZ frame;authed 后流转)
                注:login 第二阶段 unauth 状态调 backup verify 仍走 POST /gapi/public/auth/2fa/verify
                {method:"backup_code"}(legacy app_metadata 路径);D4 VwOOZ frame 主要服务
                profile 中 authed 用户管理 backup codes(generate / regenerate / count)。
                未来若改成 unauth login 也走 D4 新 endpoint,需扩 verify 支持 mfa_session_id。 */}
          <div className="flex justify-center gap-6 text-[13px]">
            <button
              type="button"
              data-testid="tqcKM-use-alt"
              onClick={() => setMethod(method === 'totp' ? 'email' : 'totp')}
              className="text-[#19caad]"
            >
              使用备用方式
            </button>
            <button
              type="button"
              data-testid="tqcKM-backup-codes-link"
              onClick={() => navigate('/profile/mfa/backup-codes')}
              className="text-[#19caad]"
            >
              备用恢复码
            </button>
            <button type="button" className="text-[#9CA3AF]">联系客服</button>
          </div>
        </form>
      </main>
    </MobileScreen>
  )
}
